import {useSelector} from 'react-redux'
import {useEffect, useCallback} from 'react'
import type {RootState} from '@/store'
import type {ThemeMode} from '@/store/slices/theme'

/**
 * Hook to track and subscribe to theme mode changes from Redux store.
 *
 * @param onChange - Optional callback that fires whenever the theme mode changes
 * @returns The current theme mode ('light' | 'dark')
 *
 * @example
 * // Basic usage - just get the current theme
 * const themeMode = useThemeMode()
 *
 * @example
 * // With change callback
 * const themeMode = useThemeMode((newMode) => {
 *   console.log('Theme changed to:', newMode)
 *   // Perform side effects when theme changes
 * })
 */
export const useThemeMode = (onChange?: (mode: ThemeMode) => void): ThemeMode => {
  const mode = useSelector((state: RootState) => state.theme.mode)

  // Memoize the callback to avoid unnecessary effect triggers
  const stableOnChange = useCallback(
    (newMode: ThemeMode) => {
      onChange?.(newMode)
    },
    [onChange],
  )

  // Fire callback when mode changes
  useEffect(() => {
    stableOnChange(mode)
  }, [mode, stableOnChange])

  return mode
}

/**
 * Hook to check if the current theme is dark mode
 * @returns true if dark mode is active
 */
export const useIsDarkMode = (): boolean => {
  const mode = useThemeMode()
  return mode === 'dark'
}

export default useThemeMode
